import React, { useMemo } from "react";

import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ShareIcon from "@mui/icons-material/Share";
import { Box, Button, CardMedia, Stack, Typography } from "@mui/material";
import { useAccountSelector } from "store";
import { IProfileUser } from "store/types/user/social";
import pickFiles from "utils/pick-files";

import { MessagePopper } from "../Message-popper/MessagePopper";

interface SocialProfileProps {
  btnHandler: () => void;
  btnTitle: string;
  btnIcon?: JSX.Element;
  profile: IProfileUser;
  userId?: number;
  userFullName?: string;
  updateAvatar?: (avatar: File) => void;
}

export const Profile: React.FC<SocialProfileProps> = ({
  btnHandler,
  btnTitle,
  profile,
  btnIcon,
  userId,
  userFullName,
  updateAvatar
}) => {
  const isSocialEnabled = useAccountSelector()?.allow_social;

  const updateAvatarHandle = async () => {
    let file: File | null = null;

    const fileList = await pickFiles();
    if (fileList?.length) {
      file = fileList[0];
    } else return; //if user clicked cancel don't send any request
    return file && updateAvatar && updateAvatar(file);
  };

  const renderAvatar = useMemo(() => {
    if (profile.profile_photo_url) {
      return (
        <CardMedia
          component="img"
          height="258px"
          sx={{ minWidth: "203px", maxWidth: "203px", borderRadius: "8px", bgcolor: "#0a3142" }}
          src={profile.profile_photo_url}
        />
      );
    } else
      return (
        <CardMedia
          sx={{
            bgcolor: "#0a3142",
            minWidth: "203px",
            maxWidth: "203px",
            minHeight: "258px",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "15px"
          }}
        >
          <AccountCircleIcon
            sx={{
              width: "115px",
              height: "115px",
              fill: "#c09560",
              stroke: "#0a3142",
              borderBlock: "#0a3142",
              marginTop: "30px"
            }}
          />
          <Button onClick={updateAvatarHandle} sx={{ backgroundColor: "#48a8d1" }} variant="contained">
            Add Photo
          </Button>
        </CardMedia>
      );
  }, [profile.profile_photo_url]);

  return (
    <Stack direction="row" justifyContent="space-between">
      {renderAvatar}
      <Stack
        sx={{
          width: "100%",
          height: "100%",
          padding: "0 0 0 20px"
        }}
        display="flex"
        direction="column"
        justifyContent="space-between"
        gap="33px"
      >
        <Stack sx={{ width: "100%", height: "100%" }} direction="row" justifyContent="space-between">
          <Stack display="flex" flexDirection="column" width="185px">
            <Typography noWrap lineHeight={1.2} sx={{ fontSize: "34px", color: "#0a3142", fontWeight: 500 }}>
              {profile.first_name}
            </Typography>
            <Typography noWrap lineHeight={1.2} sx={{ fontSize: "34px", color: "#0a3142", fontWeight: 500 }}>
              {profile.last_name}
            </Typography>
          </Stack>
          <Box
            sx={{
              borderRight: "1px solid #5d7782",
              borderLeft: "1px solid  #5d7782",
              width: "30%",
              padding: "0 20px",
              display: "flex",
              flexDirection: "column",
              gap: "8px"
            }}
          >
            {profile?.position && (
              <Stack>
                <Typography sx={{ fontSize: "14px", color: "#5d7782", lineHeight: 1, mb: "2px" }}>Position</Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#0a3142",
                    lineHeight: 1,
                    minHeight: "14px",
                    overflowWrap: "anywhere"
                  }}
                >
                  {profile?.position}
                </Typography>
              </Stack>
            )}
            {profile?.company_name && (
              <Stack>
                <Typography sx={{ fontSize: "14px", color: "#5d7782", lineHeight: 1, mb: "2px" }}>Company</Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#0a3142",
                    lineHeight: 1,
                    minHeight: "14px",
                    overflowWrap: "anywhere"
                  }}
                >
                  {profile?.company_name}
                </Typography>
              </Stack>
            )}
            {profile?.location && (
              <Stack>
                <Typography sx={{ fontSize: "14px", color: "#5d7782", lineHeight: 1, mb: "2px" }}>Location</Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#0a3142",
                    lineHeight: 1,
                    minHeight: "14px",
                    overflowWrap: "anywhere"
                  }}
                >
                  {profile?.location}
                </Typography>
              </Stack>
            )}
          </Box>
          <Box sx={{ width: "40%", justifyContent: "flex-end", alignItems: "flex-start", display: "flex" }}>
            {userId && userFullName ? (
              <MessagePopper
                title={`Connect to ${userFullName}`}
                subTitle="Introduce yourself"
                btnTitle=""
                userId={userId}
                type="connect"
              >
                <Button
                  startIcon={<ShareIcon sx={{ fontSize: "16px !important" }} />}
                  disabled={!isSocialEnabled}
                  sx={{ backgroundColor: "#26a6ae" }}
                  variant="contained"
                >
                  <Stack display="flex" flexDirection="row" alignItems="center" justifyContent="center" gap="10px">
                    {btnIcon}
                    {btnTitle}
                  </Stack>
                </Button>
              </MessagePopper>
            ) : (
              <Button
                disabled={!isSocialEnabled}
                onClick={btnHandler}
                sx={{ backgroundColor: "#48a8d1" }}
                variant="contained"
              >
                <Stack display="flex" flexDirection="row" alignItems="center" justifyContent="center" gap="10px">
                  {btnIcon}
                  {btnTitle}
                </Stack>
              </Button>
            )}
          </Box>
        </Stack>
        {!!profile?.about?.length && (
          <Stack>
            <Typography sx={{ fontSize: "16px", color: "#0a3142", fontWeight: "500" }}>About</Typography>
            <Typography
              sx={{
                lineHeight: "1.42",
                fontStyle: "italic",
                fontSize: "16px",
                color: "#0a3142",
                fontWeight: 300,
                overflowWrap: "anywhere"
              }}
            >
              {profile?.about}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
